import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import { Pressable } from "@repo/components/src/ui/pressable";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import {
  CampaignAnalyticFragmentFragment,
  DemographicsFragmentFragment,
} from "@repo/graphql/documents/campaign/fragment.generated";
import React, { useMemo, useState } from "react";
import {
  CHART_COLORS,
  DEFAULT_GENDER_COLOR,
  GENDER_COLORS,
} from "../shared/chartTokens";

type PlatformKey = "all" | "instagram" | "tiktok";

type Props = {
  report: CampaignAnalyticFragmentFragment;
};

// Narrow shape `DemographicsBody` actually reads — lets callers that don't
// have a full `CampaignAnalyticFragmentFragment` (e.g. the consolidated
// report's `ConsolidatedOrganicType`) reuse the same rendering without a cast,
// since both resolve `demographics`/`demographicsInstagram`/`demographicsTiktok`
// from the same `ReportDemographicsType` via `DemographicsFragment`.
export type DemographicsBodyProps = {
  demographics: DemographicsFragmentFragment;
  demographicsInstagram: DemographicsFragmentFragment;
  demographicsTiktok: DemographicsFragmentFragment;
};

type Slice = DemographicsFragmentFragment["gender"][number];

/** Prefer the combined view, else whichever single platform has audience data. */
const defaultPlatform = (
  all: DemographicsFragmentFragment,
  ig: DemographicsFragmentFragment,
  tt: DemographicsFragmentFragment,
): PlatformKey => {
  if (all.hasData) return "all";
  if (ig.hasData) return "instagram";
  if (tt.hasData) return "tiktok";
  return "all";
};

const hasDataFor = (
  platform: PlatformKey,
  all: DemographicsFragmentFragment,
  ig: DemographicsFragmentFragment,
  tt: DemographicsFragmentFragment,
): boolean => {
  if (platform === "instagram") return ig.hasData;
  if (platform === "tiktok") return tt.hasData;
  return all.hasData;
};

export const DemographicsBody = React.memo(
  ({
    demographics: all,
    demographicsInstagram: ig,
    demographicsTiktok: tt,
  }: DemographicsBodyProps) => {
    // Land on a platform that actually has something to show.
    const [selected, setSelected] = useState<PlatformKey>(() =>
      defaultPlatform(all, ig, tt),
    );

    // Refreshing the metrics can change which platforms have audience data
    // while this section stays mounted. Resolve the tab during render rather
    // than storing a selection that has gone stale, so nobody is stranded on
    // a tab that just emptied. A tab that still has data is honoured.
    const platform = hasDataFor(selected, all, ig, tt)
      ? selected
      : defaultPlatform(all, ig, tt);

    const active =
      platform === "instagram" ? ig : platform === "tiktok" ? tt : all;

    const tabs = (
      <PlatformTabs
        value={platform}
        onChange={setSelected}
        hasIg={ig.hasData}
        hasTt={tt.hasData}
      />
    );

    if (!active.hasData) {
      return (
        <VStack space="md">
          <HStack className="justify-end">{tabs}</HStack>
          <Text size="sm" className="text-typography-500">
            Los creadores de esta campaña no tienen datos de audiencia
            disponibles en sus cuentas vinculadas.
          </Text>
        </VStack>
      );
    }

    return (
      <VStack space="md">
        <HStack className="justify-end">{tabs}</HStack>
        {/* Every panel grows to fill whatever row it lands on. With fixed
            `w-1/3` widths, a panel that wrapped (e.g. once the Analytics
            selection rail narrows the page) sat a third of the way across
            its own row with dead space beside it. */}
        <HStack className="flex-wrap -m-1.5 items-stretch">
          <Box className="flex-1 p-1.5 min-w-[300px]">
            <PanelCard title="Género">
              <GenderPanel
                slices={active.gender}
                coveredReach={active.coveredReach}
              />
            </PanelCard>
          </Box>
          <Box className="flex-1 p-1.5 min-w-[320px]">
            <PanelCard title="Edad">
              <BarPanel slices={active.age} labelWidth="w-14" />
            </PanelCard>
          </Box>
          <Box className="flex-1 p-1.5 min-w-[300px]">
            <PanelCard title="Geografía">
              <BarPanel slices={active.geography} labelWidth="w-20" />
            </PanelCard>
          </Box>
        </HStack>
        <Coverage
          coveredReach={active.coveredReach}
          totalReach={active.totalReach}
        />
      </VStack>
    );
  },
);

DemographicsBody.displayName = "DemographicsBody";

const Demographics = React.memo(({ report }: Props) => (
  <DemographicsBody
    demographics={report.demographics}
    demographicsInstagram={report.demographicsInstagram}
    demographicsTiktok={report.demographicsTiktok}
  />
));

Demographics.displayName = "OrganicDemographics";

/**
 * Some creators publish without an audience breakdown on their linked account.
 * Their reach is real but unattributable, so it is excluded from the split — say
 * so rather than letting the percentages imply full coverage.
 */
const Coverage = ({
  coveredReach,
  totalReach,
}: {
  coveredReach: number;
  totalReach: number;
}) => {
  if (totalReach <= 0 || coveredReach >= totalReach) return null;
  const uncovered = totalReach - coveredReach;
  return (
    <Text size="2xs" className="text-typography-400">
      El desglose cubre {formatSeparators(Math.round(coveredReach))} de{" "}
      {formatSeparators(Math.round(totalReach))} cuentas alcanzadas (
      {formatPercentage(coveredReach / totalReach)}).{" "}
      {formatSeparators(Math.round(uncovered))} provienen de creadores sin datos
      demográficos en su cuenta vinculada.
    </Text>
  );
};

const PlatformTabs = ({
  value,
  onChange,
  hasIg,
  hasTt,
}: {
  value: PlatformKey;
  onChange: (v: PlatformKey) => void;
  hasIg: boolean;
  hasTt: boolean;
}) => {
  const tabs: { key: PlatformKey; label: string; disabled: boolean }[] = [
    { key: "all", label: "Todos", disabled: false },
    { key: "instagram", label: "Instagram", disabled: !hasIg },
    { key: "tiktok", label: "TikTok", disabled: !hasTt },
  ];
  return (
    <HStack space="xs" className="bg-background-100 p-1 rounded-lg">
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <Pressable
            key={tab.key}
            onPress={() => !tab.disabled && onChange(tab.key)}
            disabled={tab.disabled}
            className={
              "px-3 py-1.5 rounded-md " +
              (active && !tab.disabled ? "bg-background-0" : "bg-transparent")
            }
          >
            <Text
              size="xs"
              className={
                "text-center font-semibold " +
                (active && !tab.disabled
                  ? "text-typography-900"
                  : tab.disabled
                    ? "text-typography-300"
                    : "text-typography-500")
              }
            >
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </HStack>
  );
};

const PanelCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box className="bg-background-50 border border-outline-100 rounded-2xl px-5 py-5 h-full flex flex-col">
    <Text
      size="2xs"
      className="text-typography-500 uppercase font-semibold mb-4"
    >
      {title}
    </Text>
    <Box className="flex-1">{children}</Box>
  </Box>
);

const GenderPanel = ({
  slices,
  coveredReach,
}: {
  slices: readonly Slice[];
  coveredReach: number;
}) => {
  const segments = useMemo(
    () =>
      slices.map((s) => ({
        ...s,
        color: GENDER_COLORS[s.label] ?? DEFAULT_GENDER_COLOR,
      })),
    [slices],
  );
  if (!slices.length) return <Text size="sm">—</Text>;
  return (
    <HStack space="md" className="items-center justify-center flex-1 h-full">
      <Box className="shrink-0">
        <Donut segments={segments} size={132} centerValue={coveredReach} />
      </Box>
      <VStack space="md" className="flex-1 min-w-0">
        {segments.map((s) => (
          <HStack key={s.label} space="sm" className="items-center">
            <Box
              className="w-2.5 h-2.5 rounded shrink-0"
              style={{ backgroundColor: s.color } as object}
            />
            <Text
              size="sm"
              numberOfLines={1}
              isTruncated
              className="text-typography-700 flex-1 min-w-0"
            >
              {s.label}
            </Text>
            <Text size="sm" className="text-typography-900 font-bold shrink-0">
              {formatPercentage(s.pct)}
            </Text>
          </HStack>
        ))}
      </VStack>
    </HStack>
  );
};

const BarPanel = ({
  slices,
  labelWidth,
}: {
  slices: readonly Slice[];
  labelWidth: string;
}) => {
  if (!slices.length) return <Text size="sm">—</Text>;
  return (
    <VStack space="sm">
      {slices.map((s) => (
        <HStack key={s.label} space="sm" className="items-center">
          <Text
            size="xs"
            className={"text-typography-500 " + labelWidth}
            numberOfLines={1}
          >
            {s.label}
          </Text>
          <Box className="flex-1 h-2 bg-background-100 rounded-full overflow-hidden">
            <Box
              className="bg-brand-500 h-full"
              style={
                {
                  width: `${Math.min(100, Math.max(0, s.pct * 100))}%`,
                } as object
              }
            />
          </Box>
          <Text
            size="sm"
            className="text-typography-900 font-bold w-12 text-right"
          >
            {formatPercentage(s.pct)}
          </Text>
          <Text size="xs" className="text-typography-500 w-16 text-right">
            {formatSeparators(Math.round(s.reach))}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
};

const Donut = ({
  segments,
  size,
  centerValue,
}: {
  segments: { label: string; pct: number; color: string }[];
  size: number;
  centerValue: number;
}) => {
  const radius = 15.915; // circumference ≈ 100, so dasharray reads as a percent
  const START_OFFSET = 25; // places the first segment at 12 o'clock
  // Each segment starts where the previous ones left off, so its offset is the
  // running total of everything before it.
  const circles = segments.map((s, i) => {
    const precedingPct = segments
      .slice(0, i)
      .reduce((sum, prev) => sum + prev.pct * 100, 0);
    return (
      <circle
        key={s.label}
        cx="18"
        cy="18"
        r={radius}
        fill="none"
        stroke={s.color}
        strokeWidth="3.6"
        strokeDasharray={`${Math.max(0, s.pct * 100)} ${100 - s.pct * 100}`}
        strokeDashoffset={START_OFFSET - precedingPct}
        transform="rotate(-90 18 18)"
      />
    );
  });
  return (
    <Box
      className="relative items-center justify-center"
      style={{ width: size, height: size } as object}
    >
      <svg viewBox="0 0 36 36" width={size} height={size}>
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke={CHART_COLORS.track}
          strokeWidth="3.6"
        />
        {circles}
      </svg>
      <Box className="absolute items-center">
        <Text size="lg" className="font-bold text-typography-900">
          {formatSeparators(Math.round(centerValue))}
        </Text>
        <Text size="2xs" className="text-typography-500">
          alcanzados
        </Text>
      </Box>
    </Box>
  );
};

export default Demographics;
